import { hydrateFormAliasesFromRoleKeys, SECONDARY_ANNUITANT_KEYS } from './nischitRoleFields';

/**
 * Canonical field names for term lead contact and identity.
 * Use these keys when reading/writing Redux and API payloads to avoid email/emailId/mobile drift.
 */
export const LEAD_FIELD_MOBILE = 'mobileNumber';
export const LEAD_FIELD_EMAIL = 'emailAddress';
export const LEAD_FIELD_FULL_NAME = 'fullName';

/** Keys we never overwrite with blank when merging stage blobs (Stage 3.0 Quote has emailAddress: ""). */
const CONTACT_KEYS_NO_BLANK_OVERWRITE = new Set([
  LEAD_FIELD_EMAIL,
  LEAD_FIELD_MOBILE,
  'email',
  'emailId',
  'mobile',
  'proposerEmailAddress',
  'proposerMobileNumber',
]);

export function getLeadMobile(lead) {
  if (!lead || typeof lead !== 'object') return '';
  const direct = lead[LEAD_FIELD_MOBILE] ?? lead.mobile ?? lead.mobileNo ?? lead.proposerMobileNumber;
  if (String(direct ?? '').trim()) return String(direct).trim();
  const nested =
    lead.paymentData?.mobileNumber ??
    lead.leadInput?.mobileNumber ??
    lead.summaryData?.mobileNumber;
  return String(nested ?? '').trim();
}

export function getLeadEmail(lead) {
  if (!lead || typeof lead !== 'object') return '';
  const direct = lead[LEAD_FIELD_EMAIL] ?? lead.email ?? lead.emailId ?? lead.proposerEmailAddress;
  if (String(direct ?? '').trim()) return String(direct).trim();
  const nested =
    lead.paymentData?.emailAddress ??
    lead.paymentData?.email ??
    lead.leadInput?.emailId ??
    lead.leadInput?.emailAddress ??
    lead.leadInput?.email ??
    lead.biPayload?.liEmailid ??
    lead.biPayload?.email ??
    lead.biPayload?.emailId ??
    lead.summaryData?.emailAddress;
  return String(nested ?? '').trim();
}

export function getLeadFullName(lead) {
  if (!lead || typeof lead !== 'object') return '';
  const v = lead[LEAD_FIELD_FULL_NAME] ?? lead.name ?? lead.proposerName;
  return String(v ?? '').trim();
}

/**
 * Best-effort DOB for display and APIs — tries life-assured step, lead form, then quote / leadInput.
 * @param {object|null} lead - leadFormData
 * @param {object|null} life - stepData.lifeAssured
 * @param {object|null} quoteResult - quoteResult from Redux
 * @returns {string}
 */
export function getLeadDateOfBirth(lead, life, quoteResult) {
  const candidates = [
    life?.dateOfBirth,
    lead?.dateOfBirth,
    lead?.proposerDob,
    lead?.lifeAssuredDob,
    quoteResult?.lifeAssuredDob,
    quoteResult?.leadInput?.lifeAssuredDob,
    quoteResult?.dateOfBirth,
    quoteResult?.leadInput?.dateOfBirth,
  ];
  for (const c of candidates) {
    const s = c != null ? String(c).trim() : '';
    if (s) return s;
  }
  return '';
}

/**
 * Display / full name for header: proposal step first, then lead, then quote.
 */
export function getLeadDisplayName(lead, life, quoteResult) {
  const candidates = [
    life?.fullName,
    lead?.[LEAD_FIELD_FULL_NAME],
    lead?.proposerName,
    lead?.name,
    quoteResult?.lifeAssuredName,
    quoteResult?.leadInput?.lifeAssuredName,
  ];
  for (const c of candidates) {
    const s = c != null ? String(c).trim() : '';
    if (s) return s;
  }
  return '';
}

export function getLeadGender(lead, life, quoteResult) {
  const candidates = [
    lead?.gender,
    lead?.proposerGender,
    life?.gender,
    quoteResult?.leadInput?.lifeAssuredGender,
    quoteResult?.gender,
  ];
  for (const c of candidates) {
    const s = c != null ? String(c).trim() : '';
    if (!s) continue;
    const normalized = s.toLowerCase();
    if (s === 'F' || normalized === 'female') return 'Female';
    if (s === 'M' || normalized === 'male') return 'Male';
    if (
      s === 'T' ||
      s === 'O' ||
      normalized === 'other' ||
      normalized === 'others' ||
      normalized === 'trans' ||
      normalized.startsWith('option')
    ) {
      return 'Other';
    }
    return s;
  }
  return '';
}

/**
 * "Are you based in India?" — lead stores Yes/No (or NRI / option refs).
 * BI expects Yes/No; backend maps No → @CUSCOM 37 / @NATIONALITY false.
 * @param {...object|null} sources
 * @returns {'Yes'|'No'}
 */
export function getLeadNationality(...sources) {
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    const raw = src.nationality ?? src.basedInIndia ?? src.nriResident;
    if (raw == null || String(raw).trim() === '') continue;
    const trimmed = String(raw).trim();
    const lower = trimmed.toLowerCase();
    if (
      lower === 'yes' ||
      lower === 'true' ||
      lower === '1' ||
      lower === 'indian' ||
      trimmed === 'Option94086906'
    ) {
      return 'Yes';
    }
    if (
      lower === 'no' ||
      lower === 'false' ||
      lower === '0' ||
      lower === 'nri' ||
      trimmed === 'Option51649564'
    ) {
      return 'No';
    }
  }
  return 'Yes';
}

/**
 * State name for BI @LI_STATE / resident @CUSCOM.
 * @param {...object|null} sources
 * @returns {string}
 */
export function getLeadStateForBi(...sources) {
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    const raw =
      src.communicationState ??
      src.residencyState ??
      src.branchState ??
      src.communicationAddressState ??
      src.state ??
      src.liState;
    if (raw != null && String(raw).trim() !== '') {
      return String(raw).trim();
    }
  }
  return '';
}

/**
 * Primary annuitant relationship is self (proposer = primary).
 * Handles literal `self`, display labels, and known Liferay option refs.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isAnnuitantSelfRelationshipValue(value) {
  if (value == null || String(value).trim() === '') return true;
  const trimmed = String(value).trim();
  const lower = trimmed.toLowerCase();
  if (lower === 'self') return true;
  // Nischit lead form self option reference (see Proposal.jsx normalizeRelation).
  if (trimmed === 'Option81519591') return true;
  return false;
}

/** True when mobile looks like a valid IN mobile and email is plausible. */
export function hasLeadContactInfo(lead, quoteResult = null) {
  const m = getLeadMobile(lead || {}).replace(/\D/g, '') ||
    (quoteResult ? getLeadMobile(quoteResult).replace(/\D/g, '') : '');
  const e = getLeadEmail(lead || {}) || (quoteResult ? getLeadEmail(quoteResult) : '');
  return m.length >= 10 && e.includes('@');
}

/**
 * Merge stage blobs in numeric stage order.
 * Later stages can include empty strings for the same keys as earlier stages (e.g. Stage 3.0 Quote has
 * emailAddress: "" which must NOT wipe Stage 1.1 Lead email). We skip assigning blank over existing contact values.
 * @param {Record<string, object>|null} stagesObject - Parsed cumulative / stages object
 * @returns {Record<string, unknown>}
 */
export function mergeCumulativeStagesOrdered(stagesObject) {
  if (!stagesObject || typeof stagesObject !== 'object') return {};
  const keys = Object.keys(stagesObject);
  const scored = keys.map((k) => ({ k, n: stageKeySortValue(k) }));
  scored.sort((a, b) => a.n - b.n);
  const out = {};
  for (const { k } of scored) {
    const block = stagesObject[k];
    if (block && typeof block === 'object' && !Array.isArray(block)) {
      assignStageBlockNoBlankContactOverwrite(out, block);
    }
  }
  return hydrateFormAliasesFromRoleKeys(out);
}

function isBlankContactOverwrite(key, newVal, existingVal) {
  if (!CONTACT_KEYS_NO_BLANK_OVERWRITE.has(key)) return false;
  const empty =
    newVal == null ||
    (typeof newVal === 'string' && newVal.trim() === '');
  if (!empty) return false;
  const had =
    existingVal != null &&
    typeof existingVal === 'string' &&
    existingVal.trim() !== '';
  return had;
}

function assignStageBlockNoBlankContactOverwrite(target, block) {
  for (const [key, val] of Object.entries(block)) {
    if (isBlankContactOverwrite(key, val, target[key])) {
      continue;
    }
    target[key] = val;
  }

  const primaryLifeAssured = block?.lifeassured?.primaryLifeAssured;
  if (primaryLifeAssured && typeof primaryLifeAssured === 'object' && !Array.isArray(primaryLifeAssured)) {
    for (const [key, val] of Object.entries(primaryLifeAssured)) {
      if (isBlankContactOverwrite(key, val, target[key])) {
        continue;
      }
      target[key] = val;
    }
    if (!target.lifeAssuredDob && primaryLifeAssured.lifeAssuredDateOfBirth) {
      target.lifeAssuredDob = primaryLifeAssured.lifeAssuredDateOfBirth;
    }
    if (!target.dateOfBirth && primaryLifeAssured.lifeAssuredDateOfBirth) {
      target.dateOfBirth = primaryLifeAssured.lifeAssuredDateOfBirth;
    }
  }

  const primaryAnnuity = block?.annuitant?.primaryAnnuity || block?.annuitant?.primaryAnnuitant;
  if (primaryAnnuity && typeof primaryAnnuity === 'object' && !Array.isArray(primaryAnnuity)) {
    for (const [key, val] of Object.entries(primaryAnnuity)) {
      if (isBlankContactOverwrite(key, val, target[key])) {
        continue;
      }
      target[key] = val;
    }
  }

  const secondary = block?.annuitant?.secondaryAnnuitant;
  if (secondary && typeof secondary === 'object' && !Array.isArray(secondary)) {
    SECONDARY_ANNUITANT_KEYS.forEach((key) => {
      const val = secondary[key];
      if (val === undefined || val === null) return;
      if (typeof val === 'string' && val.trim() === '') return;
      if (target[key] == null || String(target[key]).trim() === '') {
        target[key] = val;
      }
    });
  }
}

function stageKeySortValue(key) {
  const token = String(key || '').toLowerCase().replace(/[^a-z]/g, '');
  const semanticOrder = {
    leadbasic: 1,
    leadpersonal: 2,
    leadprimaryannuitant: 3,
    leadlifeassured: 3,
    leadresidency: 4,
    leadadditional: 5,
    leadfinal: 5,
    journeygeneric: 6,
  };
  if (Object.prototype.hasOwnProperty.call(semanticOrder, token)) {
    return semanticOrder[token];
  }
  const m = String(key).match(/(\d+(?:\.\d+)?)/);
  if (m) {
    const v = parseFloat(m[1]);
    return Number.isNaN(v) ? 0 : v;
  }
  return 0;
}
